import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Sparkles, Droplets, Shield, Zap, ArrowRight } from "lucide-react";
import heroCeramic from "@/assets/hero-ceramic.jpg";

const CeramicCoatings = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Ceramic Coating Las Vegas | XPEL Fusion Plus Specialist</title>
        <meta name="description" content="Las Vegas XPEL Fusion Plus ceramic coating specialists. Superior gloss, hydrophobic protection & UV defense. 1, 4 & 8-year warranty packages. Free consultation." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={heroCeramic} 
            alt="XPEL Fusion Plus Ceramic Coating Las Vegas" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <Sparkles className="w-16 h-16 text-accent mb-6" />
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 uppercase">
              XPEL FUSION PLUS™ Ceramic Coating
            </h1>
            <p className="text-xl text-muted-foreground">
              Advanced nano-ceramic technology for unmatched gloss, protection, and durability
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center uppercase">
              Why XPEL FUSION PLUS™?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Superior Gloss</h3>
                  <p className="text-muted-foreground">
                    Enhances paint depth and creates a stunning mirror-like finish that lasts for years
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Hydrophobic Properties</h3>
                  <p className="text-muted-foreground">
                    Water and contaminants bead off instantly, making maintenance effortless
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Chemical Resistance</h3>
                  <p className="text-muted-foreground">
                    Protects against acid rain, bird droppings, tree sap, and harsh chemicals
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">UV Protection</h3>
                  <p className="text-muted-foreground">
                    Prevents paint oxidation and fading from harmful UV rays
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Easier Cleaning</h3>
                  <p className="text-muted-foreground">
                    Contaminants wash away easily, reducing wash time and effort
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Long-Lasting Protection</h3>
                  <p className="text-muted-foreground">
                    Professional-grade coatings last up to 8 years with proper maintenance
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 text-center uppercase">
              See XPEL FUSION PLUS™ In Action
            </h2>
            <p className="text-center text-muted-foreground mb-8">
              Watch how our ceramic coating delivers unmatched protection and shine
            </p>
            <div className="relative aspect-video rounded-lg overflow-hidden border border-border">
              <iframe
                src="https://www.youtube.com/embed/0kgESqYU2zY"
                title="XPEL Fusion Plus Ceramic Coating"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center uppercase">
              XPEL FUSION PLUS™ Packages
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {/* 1-Year Package */}
              <Card className="bg-card border-silver">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <Shield className="w-16 h-16 text-accent mx-auto mb-4" />
                    <h3 className="text-2xl font-heading font-bold mb-2 uppercase">1-Year Warranty</h3>
                    <p className="text-accent font-semibold text-lg">Enhanced Protection</p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Enhanced gloss finish</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Hydrophobic water beading</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Easier cleaning & maintenance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">UV & chemical protection</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* 4-Year Package */}
              <Card className="bg-card border-accent shadow-[var(--shadow-gold-glow)]">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <Shield className="w-16 h-16 text-accent mx-auto mb-4" />
                    <h3 className="text-2xl font-heading font-bold mb-2 uppercase">4-Year Warranty</h3>
                    <p className="text-accent font-semibold text-lg">Standard Protection</p>
                    <span className="inline-block mt-2 px-3 py-1 bg-accent text-accent-foreground text-xs font-heading uppercase rounded-sm">
                      Most Popular
                    </span>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Superior durability</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Advanced chemical resistance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Enhanced scratch resistance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Deep, lasting gloss</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* 8-Year Package */}
              <Card className="bg-card border-silver">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <Shield className="w-16 h-16 text-accent mx-auto mb-4" />
                    <h3 className="text-2xl font-heading font-bold mb-2 uppercase">8-Year Warranty</h3>
                    <p className="text-accent font-semibold text-lg">Ultimate Protection</p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Thickest ceramic layer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Maximum gloss enhancement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Premium self-healing properties</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Industry-leading durability</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Add-Ons Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center uppercase">
              Complete Your Protection
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-card border-silver">
                <CardContent className="p-6">
                  <Sparkles className="w-10 h-10 text-accent mb-3" />
                  <h3 className="text-xl font-heading font-bold mb-2">Wheels & Calipers</h3>
                  <p className="text-muted-foreground">
                    Brake dust resistance and easier cleaning for wheels and calipers
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-silver">
                <CardContent className="p-6">
                  <Droplets className="w-10 h-10 text-accent mb-3" />
                  <h3 className="text-xl font-heading font-bold mb-2">Glass</h3>
                  <p className="text-muted-foreground">
                    Rain repellent coating for improved visibility in wet conditions
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-silver">
                <CardContent className="p-6">
                  <Shield className="w-10 h-10 text-accent mb-3" />
                  <h3 className="text-xl font-heading font-bold mb-2">Trim & Plastic</h3>
                  <p className="text-muted-foreground">
                    Fade prevention and restoration for exterior trim and plastics
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-silver">
                <CardContent className="p-6">
                  <Zap className="w-10 h-10 text-accent mb-3" />
                  <h3 className="text-xl font-heading font-bold mb-2">Interior Upholstery</h3>
                  <p className="text-muted-foreground">
                    Stain resistance and protection for leather and fabric surfaces
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Paint Correction Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 text-center uppercase">
              Paint Correction
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Before ceramic coating application, paint correction removes imperfections to ensure a flawless finish
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-card border-silver">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-heading font-bold mb-4 uppercase">1-Step Correction</h3>
                  <p className="text-accent font-semibold mb-4">Removes 50-70% of Swirls</p>
                  <p className="text-muted-foreground mb-6">
                    Single-stage polishing process that eliminates light to moderate surface imperfections, 
                    enhancing gloss and preparing the surface for ceramic coating.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Light swirl removal</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Enhanced gloss finish</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Cost-effective solution</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-card border-silver">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-heading font-bold mb-4 uppercase">2-Step Correction</h3>
                  <p className="text-accent font-semibold mb-4">Removes 85-95% of Defects</p>
                  <p className="text-muted-foreground mb-6">
                    Advanced two-stage process combining compounding and polishing to achieve a 
                    near-perfect mirror finish, eliminating deep scratches and severe imperfections.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Deep scratch removal</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Mirror-like finish</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Show-quality results</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 text-center uppercase">
              The Battle Born Process
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Our meticulous 4-step process ensures flawless ceramic coating application
            </p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {/* Step 1 */}
              <div className="relative">
                <Card className="bg-card border-silver h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-5xl font-heading font-bold text-accent">01</span>
                      <Droplets className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 uppercase">Decontamination</h3>
                    <p className="text-sm text-muted-foreground">
                      Chemical wash and clay bar treatment to remove all surface contaminants
                    </p>
                  </CardContent>
                </Card>
                <ArrowRight className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 w-8 h-8 text-accent z-10" />
              </div>

              {/* Step 2 */}
              <div className="relative">
                <Card className="bg-card border-silver h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-5xl font-heading font-bold text-accent">02</span>
                      <CheckCircle className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 uppercase">Inspection</h3>
                    <p className="text-sm text-muted-foreground">
                      Paint thickness measurement and defect mapping with precision tools
                    </p>
                  </CardContent>
                </Card>
                <ArrowRight className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 w-8 h-8 text-accent z-10" />
              </div>

              {/* Step 3 */}
              <div className="relative">
                <Card className="bg-card border-silver h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-5xl font-heading font-bold text-accent">03</span>
                      <Sparkles className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 uppercase">Restoration</h3>
                    <p className="text-sm text-muted-foreground">
                      Paint correction and polishing to achieve a flawless surface
                    </p>
                  </CardContent>
                </Card>
                <ArrowRight className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 w-8 h-8 text-accent z-10" />
              </div>

              {/* Step 4 */}
              <div>
                <Card className="bg-card border-accent shadow-[var(--shadow-gold-glow)] h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-5xl font-heading font-bold text-accent">04</span>
                      <Shield className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 uppercase">Application</h3>
                    <p className="text-sm text-muted-foreground">
                      Precision layering of XPEL FUSION PLUS™ ceramic coating
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Enhance Your Vehicle's Beauty
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Discover the perfect ceramic coating package for your vehicle
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "XPEL FUSION PLUS Ceramic Coating",
          "provider": {
            "@type": "AutomotiveBusiness",
            "name": "Battle Born Protection Films"
          },
          "areaServed": {
            "@type": "City",
            "name": "Las Vegas"
          },
          "description": "Professional XPEL FUSION PLUS ceramic coating application providing superior gloss, hydrophobic protection, and long-lasting durability with warranty packages from 1 to 8 years."
        })}
      </script>
    </div>
  );
};

export default CeramicCoatings;
